import { GameHazard } from "@/lib/types/common/GameMode";
import { ArraySchema } from "@colyseus/schema";
import { getHazardDefinition } from "@/lib/config/hazardConfig";

/**
 * Get CSS filter string for a given hazard
 */
export function getHazardFilter(hazard: GameHazard | ""): string {
    if (!hazard) return "";
    const definition = getHazardDefinition(hazard);
    return definition?.cssFilter || "";
}

/**
 * Get CSS transform string for a given hazard
 */
export function getHazardTransform(hazard: GameHazard | ""): string {
    if (!hazard) return "";
    const definition = getHazardDefinition(hazard);
    return definition?.cssTransform || "";
}

/**
 * Get combined CSS style object for a given hazard
 */
export function getHazardStyle(hazard: GameHazard | ""): React.CSSProperties {
    const filter = getHazardFilter(hazard);
    const transform = getHazardTransform(hazard);
    
    const style: React.CSSProperties = {};
    
    if (filter) {
        style.filter = filter;
    }
    
    if (transform) {
        style.transform = transform;
    }
    
    return style;
}

/**
 * Get combined CSS style object for multiple hazards
 */
export function getMultipleHazardsStyle(hazards: ArraySchema<string> | string[]): React.CSSProperties {
    if (!hazards || hazards.length === 0) {
        return {};
    }

    // Helper to parse filter functions and keep the highest value for each type
    const filterMax: Record<string, string> = {};
    const transforms: string[] = [];

    const hazardArray = Array.isArray(hazards) ? hazards : Array.from(hazards);
    hazardArray.forEach(hazard => {
        const definition = getHazardDefinition(hazard as GameHazard);

        if (definition?.cssFilter) {
            // Match filter functions like brightness(1.3)
            const filterRegex = /([a-zA-Z-]+)\(([^)]+)\)/g;
            let match;
            while ((match = filterRegex.exec(definition.cssFilter)) !== null) {
                const type = match[1];
                const value = match[2];
                // Only handle numeric values for comparison
                const numValue = parseFloat(value);
                if (!isNaN(numValue)) {
                    if (
                        !(type in filterMax) ||
                        numValue > parseFloat(filterMax[type])
                    ) {
                        filterMax[type] = value;
                    }
                } else {
                    // For non-numeric filters, just keep the first occurrence
                    if (!(type in filterMax)) {
                        filterMax[type] = value;
                    }
                }
            }
        }

        if (definition?.cssTransform) {
            transforms.push(definition.cssTransform);
        }
    });
    
    const style: React.CSSProperties = {};

    // Combine all filters, using only the highest value for each type
    const filterStrings = Object.entries(filterMax).map(
        ([type, value]) => `${type}(${value})`
    );
    if (filterStrings.length > 0) {
        style.filter = filterStrings.join(" ");
    }

    // Combine all transforms with spaces
    if (transforms.length > 0) {
        style.transform = transforms.join(" ");
    }

    return style;
}

